import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import './Institution.css';
import axiosInstance from '../../api/axiosInstance';

const Institution = () => {
  const [formData, setFormData] = useState({
    institutionName: '',
    contactPerson: '',
    designation: '',
    officialEmail: '',
    mobileNumber: '',
    whatsappNumber: '',
    city: '',
    state: '',
    serviceRequired: '',
    projectDetails: '',
    preferredContactTime: '',
  });

  const [loading, setLoading] = useState(false);
  const [successBanner, setSuccessBanner] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const servicesOptions = [
    'New Medical College Establishment',
    'MBBS Seat Expansion',
    'New PG (MD/MS) Courses',
    'PG Seat Expansion',
    'Institutional & Teaching Hospital Compliance',
    'Documentation & Institutional Readiness',
    'Medical Faculty Recruitment',
    'Other Professional Requirement',
  ];

  const contactTimeOptions = [
    'Morning (09:00 AM – 12:00 PM)',
    'Afternoon (12:00 PM – 04:00 PM)',
    'Evening (04:00 PM – 07:30 PM)',
    'Anytime during business hours',
  ];

  const advisoryRoadmap = [
    {
      step: '01',
      title: 'Confidential Evaluation & NDA',
      desc: 'Promoter strategies, institutional data, and proposed project timelines are strictly protected under non-disclosure protocols.',
    },
    {
      step: '02',
      title: 'Diagnostic Gap Assessment',
      desc: 'Methodical review of teaching hospital beds, clinical material, academic infrastructure, and statutory documentation readiness.',
    },
    {
      step: '03',
      title: 'Turnkey Execution & Faculty Support',
      desc: 'Deployment of qualified full-time medical faculty and structured compliance roadmaps tailored to your target academic intake.',
    },
  ];

  // Auto-dismiss success banner after 10s
  useEffect(() => {
    let timer;
    if (successBanner) {
      timer = setTimeout(() => setSuccessBanner(false), 10000);
    }
    return () => clearTimeout(timer);
  }, [successBanner]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Mobile & WhatsApp inputs: digits only, max 10 digits
    if (name === 'mobileNumber' || name === 'whatsappNumber') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: digitsOnly }));
      if (errorMsg) setErrorMsg('');
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errorMsg) setErrorMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    // Indian Mobile Number Check (10 digits starting with 6, 7, 8, 9)
    const indianMobileRegex = /^[6-9]\d{9}$/;
    if (!indianMobileRegex.test(formData.mobileNumber.trim())) {
      setErrorMsg('Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9.');
      return;
    }

    if (formData.whatsappNumber.trim() !== '') {
      if (!indianMobileRegex.test(formData.whatsappNumber.trim())) {
        setErrorMsg('Please enter a valid 10-digit WhatsApp number starting with 6, 7, 8, or 9 (or leave blank).');
        return;
      }
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(formData.officialEmail.trim())) {
      setErrorMsg('Please enter a valid official email address.');
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post('/api/institution/submit', formData);
      if (response.data?.success || response.status === 201 || response.status === 200) {
        setSuccessBanner(true);
        setErrorMsg('');
        
        setFormData({
          institutionName: '',
          contactPerson: '',
          designation: '',
          officialEmail: '',
          mobileNumber: '',
          whatsappNumber: '',
          city: '',
          state: '',
          serviceRequired: '',
          projectDetails: '',
          preferredContactTime: '',
        });
      }
    } catch (err) {
      console.error('Institutional Submit Error:', err);
      const msg = err.response?.data?.message || 'Unable to submit enquiry. Please check your connection and try again.';
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>For Institutions | Nutrivio</title>
        <meta
          name="description"
          content="Partner with Nutrivio for new medical college establishment, institutional compliance, documentation readiness, MBBS and PG expansion, and faculty recruitment support."
        />
      </Helmet>

      <div className="for-institutions-page-wrapper">
        <div className="for-institutions-page-ambient-pattern"></div>

        <div className="for-institutions-page-container">
          <div className="for-institutions-page-layout-grid">
            
            {/* ================= LEFT COLUMN: STICKY INFO ================= */}
            <aside className="for-institutions-page-info-col">
              <div className="for-institutions-page-glow-accent"></div>

              {/* 1. Header Card */}
              <div className="for-institutions-card-box for-institutions-intro-card">
                <div className="for-institutions-page-badge">
                  <span className="for-institutions-page-pulse-dot"></span>
                  <span>Strategic Institutional Advisory</span>
                </div>

                <h1 className="for-institutions-page-main-title">
                  Tell Us About Your <br />
                  <span className="for-institutions-page-gradient-text">Institutional Requirement</span>
                </h1>

                <p className="for-institutions-page-description">
                  Whether you are planning a New Medical College, expanding academic seats, strengthening hospital readiness, or recruiting Medical Faculty, our advisors are here to assist.
                </p>

                <div className="for-institutions-page-trust-bar">
                  <div className="for-institutions-page-trust-pill">
                    <span className="for-institutions-page-check">✓</span>
                    <span>Pan-India Scope</span>
                  </div>
                  <div className="for-institutions-page-trust-pill">
                    <span className="for-institutions-page-check">✓</span>
                    <span>100% Verified Faculty</span>
                  </div>
                  <div className="for-institutions-page-trust-pill">
                    <span className="for-institutions-page-check">✓</span>
                    <span>Statutory Compliance</span>
                  </div>
                </div>
              </div>

              {/* 2. Roadmap Card */}
              <div className="for-institutions-card-box for-institutions-page-roadmap-box">
                <div className="for-institutions-page-roadmap-header">
                  <h6>How We Process Your Requirement</h6>
                  <span>Institutional Protocol</span>
                </div>

                <div className="for-institutions-page-timeline">
                  {advisoryRoadmap.map((item, index) => (
                    <div key={index} className="for-institutions-page-timeline-item">
                      <div className="for-institutions-page-timeline-marker">
                        <span className="for-institutions-page-step-num">{item.step}</span>
                        {index < advisoryRoadmap.length - 1 && <div className="for-institutions-page-timeline-line"></div>}
                      </div>
                      <div className="for-institutions-page-timeline-content">
                        <h5>{item.title}</h5>
                        <p>{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 3. SLA Card */}
              <div className="for-institutions-page-executive-card">
                <div className="for-institutions-page-card-top-row">
                  <div className="for-institutions-page-shield-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="for-institutions-page-sla-badge">Response SLA: 24 – 48 Hours</div>
                    <h4 className="for-institutions-page-sla-title">Dedicated Senior Advisor Assignment</h4>
                  </div>
                </div>
                <p className="for-institutions-page-sla-desc">
                  Your enquiry is directly routed to healthcare planners and senior medical education advisors for a discovery discussion.
                </p>
              </div>
            </aside>

            {/* ================= RIGHT COLUMN: FORM ================= */}
            <main className="for-institutions-page-form-col">
              <div className="for-institutions-page-card">
                
                <div className="for-institutions-page-card-header">
                  <div className="for-institutions-page-form-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10 9 9 9 8 9"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="for-institutions-page-form-title">Institutional Enquiry Form</h3>
                    <p className="for-institutions-page-form-subtitle">Fill in your details to initiate an evaluation.</p>
                  </div>
                </div>

                {/* Success Banner */}
                {successBanner && (
                  <div className="institution-one-line-success-banner" role="alert">
                    <div className="institution-banner-content-left">
                      <span className="institution-banner-check">✓</span>
                      <span className="institution-banner-text">
                        Enquiry submitted successfully! A Senior Advisor will connect with you shortly.
                      </span>
                    </div>
                    <button
                      type="button"
                      className="institution-banner-close-btn"
                      onClick={() => setSuccessBanner(false)}
                      aria-label="Close"
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Error Banner */}
                {errorMsg && (
                  <div className="institution-one-line-error-banner" role="alert">
                    <div className="institution-banner-content-left">
                      <span>⚠️</span>
                      <span className="institution-banner-text">{errorMsg}</span>
                    </div>
                    <button
                      type="button"
                      className="institution-banner-close-btn error"
                      onClick={() => setErrorMsg('')}
                      aria-label="Close"
                    >
                      ✕
                    </button>
                  </div>
                )}

                <form className="for-institutions-page-form" onSubmit={handleSubmit}>
                  
                  {/* Field: Organisation Name */}
                  <div className="for-institutions-page-form-group">
                    <label className="for-institutions-page-label">
                      Organisation / Institution Name <span className="for-institutions-page-required">*</span>
                    </label>
                    <input 
                      type="text" 
                      name="institutionName" 
                      className="for-institutions-page-input"
                      placeholder="Enter organisation or institution name"
                      value={formData.institutionName}
                      onChange={handleChange}
                      required 
                      disabled={loading}
                    />
                  </div>

                  {/* Field Row: Full Name & Designation */}
                  <div className="for-institutions-page-form-row">
                    <div className="for-institutions-page-form-group">
                      <label className="for-institutions-page-label">
                        Your Name <span className="for-institutions-page-required">*</span>
                      </label>
                      <input 
                        type="text" 
                        name="contactPerson" 
                        className="for-institutions-page-input"
                        placeholder="Enter your name"
                        value={formData.contactPerson}
                        onChange={handleChange}
                        required 
                        disabled={loading}
                      />
                    </div>
                    
                    <div className="for-institutions-page-form-group">
                      <label className="for-institutions-page-label">
                        Designation <span className="for-institutions-page-required">*</span>
                      </label>
                      <input 
                        type="text" 
                        name="designation" 
                        className="for-institutions-page-input"
                        placeholder="Enter your designation"
                        value={formData.designation}
                        onChange={handleChange}
                        required 
                        disabled={loading}
                      />
                    </div>
                  </div>

                  {/* Field Row: Official Email & Mobile Number */}
                  <div className="for-institutions-page-form-row">
                    <div className="for-institutions-page-form-group">
                      <label className="for-institutions-page-label">
                        Official Email <span className="for-institutions-page-required">*</span>
                      </label>
                      <input 
                        type="email" 
                        name="officialEmail" 
                        className="for-institutions-page-input"
                        placeholder="Enter official email address"
                        value={formData.officialEmail}
                        onChange={handleChange}
                        required 
                        disabled={loading}
                      />
                    </div>
                    
                    <div className="for-institutions-page-form-group">
                      <label className="for-institutions-page-label">
                        Mobile Number <span className="for-institutions-page-required">*</span>
                      </label>
                      <input 
                        type="tel" 
                        name="mobileNumber" 
                        className="for-institutions-page-input"
                        placeholder="Enter 10-digit mobile number"
                        value={formData.mobileNumber}
                        onChange={handleChange}
                        maxLength={10}
                        required 
                        disabled={loading}
                      />
                    </div>
                  </div>

                  {/* Field Row: WhatsApp Number & Preferred Contact Time */}
                  <div className="for-institutions-page-form-row">
                    <div className="for-institutions-page-form-group">
                      <label className="for-institutions-page-label">WhatsApp Number (Optional)</label>
                      <input 
                        type="tel" 
                        name="whatsappNumber" 
                        className="for-institutions-page-input"
                        placeholder="Enter 10-digit WhatsApp number"
                        value={formData.whatsappNumber}
                        onChange={handleChange}
                        maxLength={10}
                        disabled={loading}
                      />
                    </div>
                    
                    <div className="for-institutions-page-form-group">
                      <label className="for-institutions-page-label">Preferred Contact Time</label>
                      <select 
                        name="preferredContactTime" 
                        className="for-institutions-page-select"
                        value={formData.preferredContactTime}
                        onChange={handleChange}
                        disabled={loading}
                      >
                        <option value="">Select preferred contact time</option>
                        {contactTimeOptions.map((time, idx) => (
                          <option key={idx} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Field Row: City & State */}
                  <div className="for-institutions-page-form-row">
                    <div className="for-institutions-page-form-group">
                      <label className="for-institutions-page-label">
                        City <span className="for-institutions-page-required">*</span>
                      </label>
                      <input 
                        type="text" 
                        name="city" 
                        className="for-institutions-page-input"
                        placeholder="Enter city"
                        value={formData.city}
                        onChange={handleChange}
                        required 
                        disabled={loading}
                      />
                    </div>
                    
                    <div className="for-institutions-page-form-group">
                      <label className="for-institutions-page-label">
                        State <span className="for-institutions-page-required">*</span>
                      </label>
                      <input 
                        type="text" 
                        name="state" 
                        className="for-institutions-page-input"
                        placeholder="Enter state"
                        value={formData.state}
                        onChange={handleChange}
                        required 
                        disabled={loading}
                      />
                    </div>
                  </div>

                  {/* Field: Service Required */}
                  <div className="for-institutions-page-form-group">
                    <label className="for-institutions-page-label">
                      Service Required <span className="for-institutions-page-required">*</span>
                    </label>
                    <select 
                      name="serviceRequired" 
                      className="for-institutions-page-select"
                      value={formData.serviceRequired}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    >
                      <option value="">Select service required</option>
                      {servicesOptions.map((service, index) => (
                        <option key={index} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>

                  {/* Field: Brief Project Details */}
                  <div className="for-institutions-page-form-group">
                    <label className="for-institutions-page-label">Project Details / Requirements (Optional)</label>
                    <textarea 
                      name="projectDetails" 
                      rows="4" 
                      className="for-institutions-page-textarea"
                      placeholder="Enter brief project or requirement details..."
                      value={formData.projectDetails}
                      onChange={handleChange}
                      disabled={loading}
                    ></textarea>
                  </div>

                  {/* Submit Button */}
                  <button type="submit" className="for-institutions-page-submit-btn" disabled={loading}>
                    {loading ? (
                      <span>TRANSMITTING ENQUIRY...</span>
                    ) : (
                      <>
                        <span>SUBMIT INSTITUTIONAL ENQUIRY</span>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M5 12h14M12 5l7 7-7 7"/>
                        </svg>
                      </>
                    )}
                  </button>

                  <p className="for-institutions-page-privacy-note">
                    🔒 Protected under institutional non-disclosure protocol.
                  </p>

                </form>

              </div>
            </main>

          </div>
        </div>
      </div>
    </>
  );
};

export default Institution;